import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PRESET_PERCENTS = [10, 20, 30, 40, 50, 100];

export default function OwnerCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    code: "",
    discount_percent: 10,
    max_uses: "",
    expires_at: "",
    note: "",
  });

  const refresh = async () => {
    const { data } = await api.get("/admin/coupons");
    setCoupons(data.coupons);
  };

  useEffect(() => {
    (async () => {
      try { await refresh(); } finally { setLoading(false); }
    })();
  }, []);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.code.trim()) {
      toast.error("Enter a code");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        discount_percent: Number(form.discount_percent),
        ...(form.max_uses ? { max_uses: Number(form.max_uses) } : {}),
        ...(form.expires_at ? { expires_at: new Date(form.expires_at).toISOString() } : {}),
        ...(form.note ? { note: form.note } : {}),
      };
      await api.post("/admin/coupons", payload);
      toast.success(`Coupon ${payload.code} created`);
      setForm({ code: "", discount_percent: 10, max_uses: "", expires_at: "", note: "" });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create coupon");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (code, active) => {
    try {
      await api.patch(`/admin/coupons/${code}`, { active: !active });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const remove = async (code) => {
    if (!confirm(`Delete coupon ${code}? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/coupons/${code}`);
      toast.success(`Deleted ${code}`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const generateCode = () => {
    const pct = form.discount_percent;
    const suffix = Math.random().toString(36).slice(2, 6).toUpperCase();
    const prefix = pct === 100 ? "FREE" : `SAVE${pct}`;
    setForm((p) => ({ ...p, code: `${prefix}-${suffix}` }));
  };

  if (loading) return <div className="text-[#5C6B61]">Loading coupons…</div>;

  return (
    <section className="space-y-5" data-testid="owner-coupons-section">
      <div>
        <div className="ff-label">Discount codes</div>
        <h2 className="text-2xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
          Coupons
        </h2>
        <p className="text-sm text-[#5C6B61] mt-1">
          Create % off codes (or a free pass) and share them with anyone. Codes work on every plan.
        </p>
      </div>

      <div className="ff-card p-5 space-y-4">
        <div className="ff-label">Create coupon</div>
        <div className="flex flex-wrap gap-2">
          {PRESET_PERCENTS.map((p) => (
            <button
              key={p}
              onClick={() => setField("discount_percent", p)}
              data-testid={`coupon-preset-${p}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                Number(form.discount_percent) === p
                  ? "bg-[#3B5A3E] text-white border-[#3B5A3E]"
                  : "bg-white text-[#2A332C] border-[#E5E2DA] hover:border-[#3B5A3E]"
              }`}
            >
              {p === 100 ? "Free" : `${p}% off`}
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Code">
            <div className="flex gap-2">
              <input
                type="text"
                value={form.code}
                onChange={(e) => setField("code", e.target.value.toUpperCase())}
                placeholder="WELCOME10"
                className="ff-input text-sm flex-1"
                data-testid="coupon-code-input"
              />
              <button
                type="button"
                onClick={generateCode}
                className="ff-btn-ghost text-xs px-3 border border-[#E5E2DA]"
                data-testid="coupon-generate-btn"
              >
                Generate
              </button>
            </div>
          </Field>
          <Field label="Discount %">
            <input
              type="number"
              min="1"
              max="100"
              value={form.discount_percent}
              onChange={(e) => setField("discount_percent", e.target.value)}
              className="ff-input text-sm w-full"
            />
          </Field>
          <Field label="Max uses (optional)">
            <input
              type="number"
              min="1"
              value={form.max_uses}
              onChange={(e) => setField("max_uses", e.target.value)}
              placeholder="Unlimited"
              className="ff-input text-sm w-full"
            />
          </Field>
          <Field label="Expires (optional)">
            <input
              type="date"
              value={form.expires_at}
              onChange={(e) => setField("expires_at", e.target.value)}
              className="ff-input text-sm w-full"
            />
          </Field>
          <Field label="Note (private, optional)">
            <input
              type="text"
              value={form.note}
              onChange={(e) => setField("note", e.target.value)}
              placeholder="e.g. Launch promo"
              className="ff-input text-sm w-full"
              maxLength={200}
            />
          </Field>
        </div>
        <button
          onClick={submit}
          disabled={creating}
          data-testid="coupon-create-btn"
          className="ff-btn-primary text-sm disabled:opacity-60"
        >
          {creating ? "Creating…" : "Create coupon"}
        </button>
      </div>

      <div className="ff-card p-5">
        <div className="ff-label">Existing codes ({coupons.length})</div>
        {coupons.length === 0 ? (
          <p className="text-sm text-[#5C6B61] mt-2">No coupons yet — create one above.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm" data-testid="coupon-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[#8B9690]">
                  <th className="py-2 pr-3">Code</th>
                  <th className="py-2 pr-3">Discount</th>
                  <th className="py-2 pr-3">Uses</th>
                  <th className="py-2 pr-3">Expires</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((c) => (
                  <tr key={c.code} className="border-t border-[#F0EDE7]" data-testid={`coupon-row-${c.code}`}>
                    <td className="py-3 pr-3 font-mono font-semibold text-[#2A332C]">{c.code}</td>
                    <td className="py-3 pr-3">{c.discount_percent === 100 ? "FREE" : `${c.discount_percent}% off`}</td>
                    <td className="py-3 pr-3 text-[#5C6B61]">{c.uses_count}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                    <td className="py-3 pr-3 text-[#5C6B61]">{c.expires_at ? new Date(c.expires_at).toLocaleDateString("en-GB") : "—"}</td>
                    <td className="py-3 pr-3">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${c.active ? "bg-[#E8F0E9] text-[#3B5A3E]" : "bg-[#F3F0EA] text-[#8B9690]"}`}>
                        {c.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="py-3 pr-3 flex gap-2 whitespace-nowrap">
                      <button
                        onClick={() => toggleActive(c.code, c.active)}
                        data-testid={`coupon-toggle-${c.code}`}
                        className="text-xs text-[#3B5A3E] hover:underline"
                      >
                        {c.active ? "Disable" : "Enable"}
                      </button>
                      <button
                        onClick={() => remove(c.code)}
                        data-testid={`coupon-delete-${c.code}`}
                        className="text-xs text-[#A65541] hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-[#5C6B61] font-medium mb-1">{label}</div>
      {children}
    </label>
  );
}
